import React, { useCallback, useEffect, useState } from 'react';
import {
	Table,
	TableRow,
	TableBody,
	TableCell,
	TableContainer,
	TablePagination,
	TableHead,
	TableFooter,
	TextField,
	MenuItem,
	Select,
	Grid,
	FormControl
} from '@mui/material';
import useCommonStyles from '../../../../commonStyles';
import Loader from '../../../../Components/Loader';
import { toast } from 'react-toastify';
import { Box } from '@mui/system';

/**
 * @name FilterCustomTable
 *
 * @description  A reusable table component that allows filtering and searching of rowsData, with support for pagination.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.rowsData - Contains the list of rowsData
 * @param {Array} props.columnsConfig - The configuration for table columns, including header , key, type
 * @param {number} props.rowsPerPage - Initial number of rows to be displayed per page.
 * @param {Array} props.rowsPerPageOptions - Options for the number of rows per page.
 * @param {boolean} props.dataLoaded - Flag indicating if the rowsData  has been loaded.
 * @param {Function} props.handleRowClick - Function to handle row click events.
 * @param {boolean} [props.isDropDownFilterEnable=false] - Flag indicating if filtering is enabled.
 * @param {Array} [props.filterOptions=[]] - The sections available for filtering.
 * @param {Function} [props.filterFunction=() => true] - Custom function to filter rowsData based on selected criteria.
 * @param {Function} props.handleRowClick - Function to handle row click events.
 * @param {string} props.labelVariant - The variant style for the label component variants can be- ghost | filled | outlined etc
 * @param {string} props.searchLabel - Custom Search Input Placeholder
 *
 * */
export default function FilterCustomTable(props) {
	const commonStylesClasses = useCommonStyles();

	const {
		rowsData,
		columnsConfig,
		rowsPerPage,
		rowsPerPageOptions,
		dataLoaded = false,
		handleRowClick,
		isDropDownFilterEnable = false,
		filterOptions = [],
		filterFunction = () => true,
		searchFunction = () => true,
		searchLabel = 'Search'
	} = props;

	// Component State
	const [page, setPage] = useState(0);
	const [rowItemsPerPage, setRowItemsPerPage] = useState(rowsPerPage);
	const [filteredRows, setFilteredRows] = useState(rowsData);
	const [filterValues, setFilterValues] = useState({ selectedFilter: '' });
	const [searched, setSearched] = useState('');

	const handlePageChange = (e, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (e) => {
		setRowItemsPerPage(parseInt(e.target.value, 10));
		setPage(0);
	};

	const handleCustomFilter = (field, value) => {
		setFilterValues((prev) => ({ ...prev, [field]: value }));
	};

	const handleRowSearch = useCallback(
		(value) => {
			setSearched(value);

			if (isDropDownFilterEnable && filterOptions.length === 0) {
				toast.error('Please provide filterOptions in props as isDropDownFilterEnable is true', {
					position: 'top-right'
				});
				return;
			}

			const searchedValue = value.trim().toLowerCase();
			let searchResults = rowsData;

			searchResults = rowsData.filter((row) => {
				const dropDownFilter =
					isDropDownFilterEnable &&
					filterValues.selectedFilter !== '' &&
					filterFunction(row, filterValues, filterOptions);

				const searchFilter = searchFunction(searchedValue, row);

				return dropDownFilter && searchFilter;
			});

			setFilteredRows(searchResults);
			setPage(0);
		},
		[searched]
	);

	useEffect(() => {
		if (rowsData) {
			setFilteredRows(rowsData);
			setFilterValues({});
			setSearched('');
		}
	}, [rowsData]);

	useEffect(() => {
		let filtered = rowsData;

		if (isDropDownFilterEnable)
			filtered = rowsData.filter((row) => filterFunction(row, filterValues, filterOptions));

		setFilteredRows(filtered);
		setSearched('');
		setPage(0);
	}, [filterValues, filterOptions, filterFunction]);

	return (
		<Box className={commonStylesClasses.mediumMarginAround}>
			{!isDropDownFilterEnable && (
				<TextField
					fullWidth
					style={{ width: '95%' }}
					label={searchLabel}
					value={searched}
					onChange={(e) => handleRowSearch(e.target.value)}
				/>
			)}

			{isDropDownFilterEnable && (
				<Grid container item sm={12}>
					<Grid item sm={8}>
						<TextField
							style={{ width: '95%' }}
							label={searchLabel}
							value={searched}
							onChange={(e) => handleRowSearch(e.target.value)}
						/>
					</Grid>

					<Grid item sm={4}>
						<FormControl style={{ width: '80%' }}>
							<Select
								value={filterValues.selectedFilter || ''}
								onChange={(e) => handleCustomFilter('selectedFilter', e.target.value)}
							>
								{filterOptions.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				</Grid>
			)}

			{dataLoaded && (
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								{columnsConfig.map((col) => (
									<TableCell key={col.key}>
										<b>{col.header}</b>
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredRows
								.slice(page * rowItemsPerPage, page * rowItemsPerPage + rowItemsPerPage)
								.map((row, rowIndex) => {
									return (
										<TableRow hover onClick={() => handleRowClick(row)} key={rowIndex}>
											{columnsConfig.map((col, index) => (
												<React.Fragment key={`${row[index]}-${col.key}`}>
													{!col.conditionalRendering && <TableCell>{row[col.key]}</TableCell>}
													{col.conditionalRendering && (
														<TableCell>{col.conditionalRendering(row)}</TableCell>
													)}
												</React.Fragment>
											))}
										</TableRow>
									);
								})}
						</TableBody>
						<TableFooter>
							<TableRow>
								<TablePagination
									rowsPerPageOptions={rowsPerPageOptions}
									count={filteredRows.length}
									rowsPerPage={rowItemsPerPage}
									page={page}
									onPageChange={handlePageChange}
									onRowsPerPageChange={handleChangeRowsPerPage}
								/>
							</TableRow>
						</TableFooter>
					</Table>
				</TableContainer>
			)}

			{!dataLoaded && <Loader />}
		</Box>
	);
}
